import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiCheckCircleDuotone, PiPlus, PiTrash } from "react-icons/pi";

export default function FeaturesGrid({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    const features = content.features || [
        { title: "Feature 1", description: "Description 1" },
        { title: "Feature 2", description: "Description 2" },
        { title: "Feature 3", description: "Description 3" },
    ];

    const addFeature = () => {
        const newFeatures = [...features, { title: "New Feature", description: "Description" }];
        onContentChange?.({ ...content, features: newFeatures });
    };

    const removeFeature = (idx: number) => {
        const newFeatures = features.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, features: newFeatures });
    };

    const updateFeature = (idx: number, field: string, value: string) => {
        const newFeatures = [...features];
        newFeatures[idx] = { ...newFeatures[idx], [field]: value };
        onContentChange?.({ ...content, features: newFeatures });
    };

    return (
        <div className="mx-auto max-w-5xl" style={{ fontFamily }}>
            <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
                <EditableText
                    value={content.title || "Our Features"}
                    onChange={(val) => onContentChange?.({ ...content, title: val })}
                    as="span"
                    placeholder="Section Title"
                />
            </h2>

            <div className="grid gap-8 md:grid-cols-3">
                {features.map((feature: any, idx: number) => (
                    <div
                        key={idx}
                        className="group relative rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-6 shadow-sm transition-all hover:shadow-lg hover:border-blue-200"
                    >
                        {isSelected && (
                            <button
                                onClick={(e) => { e.stopPropagation(); removeFeature(idx); }}
                                className="absolute -top-2 -right-2 hidden group-hover:flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-500 hover:bg-red-200"
                                title="Remove Feature"
                            >
                                <PiTrash className="h-3 w-3" />
                            </button>
                        )}

                        <div className="flex-shrink-0 mb-4 h-14 w-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25">
                            <PiCheckCircleDuotone className="h-7 w-7" />
                        </div>

                        <div className="flex-1 min-w-0">
                            <h3 className="mb-2 text-lg font-semibold text-gray-900">
                                <EditableText
                                    value={feature.title}
                                    onChange={(val) => updateFeature(idx, "title", val)}
                                    as="span"
                                    placeholder="Feature Title"
                                />
                            </h3>
                            <p className="text-sm text-gray-600">
                                <EditableText
                                    value={feature.description}
                                    onChange={(val) => updateFeature(idx, "description", val)}
                                    as="span"
                                    placeholder="Feature Description"
                                />
                            </p>
                        </div>
                    </div>
                ))}

                {isSelected && (
                    <button
                        onClick={addFeature}
                        className="flex min-h-[200px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 transition-all"
                    >
                        <PiPlus className="h-8 w-8 mb-2" />
                        <span className="text-sm font-medium">Add Feature</span>
                    </button>
                )}
            </div>
        </div>
    );
}
